/**
 * fsMessaging.js — invio/ricezione forward-secret sopra il vero sendMessage.
 * ⚠️ DA AUDIRE. Cutover sul canale TESTO 1:1 (wire v2 = ratchet).
 *
 *   sendFsMessage(): carica/apre la sessione, cifra col ratchet, invia l'envelope
 *     v2 (+ fsPre col 1° messaggio) + copia-per-sé. TRANSAZIONALE: avanza lo stato
 *     solo se l'invio riesce (clona → cifra → invia → salva).
 *   decryptFsMessage(): apre la sessione dal prekey-message (1° msg) o la carica,
 *     decifra su una COPIA e ripersiste solo a decifratura riuscita (retry-safe);
 *     la OPK si consuma solo dopo il successo.
 *   ensureMyPrekeysPublished(): all'onboarding/login pubblica le prekey se il
 *     device non ne ha (o sono quasi finite) e persiste i privati.
 *
 * Identità = keypair box già derivata (ChatScreen) o mnemonica (test).
 * Stato e privati prekey vivono in fsStore (IndexedDB, solo su questo device).
 * NB: allegati e copia-per-sé restano sul box statico (migrazione a parte).
 */

import { callFn } from './firebase';
import { toBase64Url, fromBase64Url } from './crypto';
import { serializeHeader, parseHeader } from './ratchet';
import { openOutgoing, openIncoming, encryptMsg, decryptMsg, publishMyPrekeys } from './fsSession';
import { saveSession, loadSession, loadMySpk, loadMyPq, peekMyOpk, consumeMyOpk, saveMyPrekeys, myOpkCount } from './fsStore';
import { encryptForSelf } from './messaging';
import { compressImage, validateVideo, generateAttachmentKey, encryptBlobToStream, uploadEncryptedBlob, ensureSodium } from './attachments';

const convKeyOf = (sigil) => String(sigil || '').toLowerCase().replace(/^@/, '');
const clone = (o) => (typeof structuredClone === 'function' ? structuredClone(o) : o);
const MAX_FILE_BYTES = 75 * 1024 * 1024;

// ── Padding: nasconde la LUNGHEZZA del messaggio portandola a un "secchio"
// fisso (in byte). Un osservatore vede solo il secchio, non la dimensione vera.
// Buckets scelti per restare sotto il limite del campo fsCt lato server.
const PAD_BUCKETS = [256, 512, 1024, 2048];
const PAD_DELIM = '';
function padText(t) {
  const len = new TextEncoder().encode(t).length;
  const target = PAD_BUCKETS.find((b) => b > len) || (Math.ceil((len + 1) / 1024) * 1024);
  return t + String.fromCharCode(1) + ' '.repeat(target - len - 1); // ' ' = 1 byte
}
function unpadText(p) {
  const i = p.indexOf(String.fromCharCode(1));
  return i >= 0 ? p.slice(0, i) : p;
}

/**
 * Invia un messaggio di testo forward-secret. Lancia 'PEER_PREKEYS_MISSING' se
 * il destinatario non ha pubblicato prekey (il chiamante può fare fallback).
 * @param {object} p
 * @param {object} [p.boxKeypair] {pub,priv} identità box (preferito)
 * @param {string} [p.mnemonic]   alternativa (test)
 * @param {string} p.recipientUid
 * @param {string} p.recipientSigil
 * @param {string} [p.senderSigil]
 * @param {string} p.text
 * @param {number} p.senderTTL
 * @param {string|Uint8Array} [p.myBoxPub]   per la copia-per-sé
 * @param {Uint8Array} [p.mySecKeyBox]
 */
export async function sendFsMessage({ mnemonic, boxKeypair, recipientUid, recipientSigil, senderSigil, text, senderTTL, myBoxPub, mySecKeyBox }) {
  if (!text || !text.length) throw new Error('TEXT_EMPTY');
  if (text.length > 500) throw new Error('TEXT_TOO_LONG');
  const convKey = convKeyOf(recipientSigil);

  let session = await loadSession(convKey);
  let fsPre = null;
  if (!session) {
    const o = await openOutgoing({ mnemonic, boxKeypair, peerSigil: recipientSigil });
    session = o.session;
    fsPre = o.prekeyMsg;
  }
  // Avanza su una COPIA: se l'invio fallisce, lo stato non si desincronizza.
  const working = clone(session);
  const m = await encryptMsg(working, padText(text)); // padding: nasconde la lunghezza

  const payload = {
    recipientUid,
    v: 2,
    fsHeader: await toBase64Url(serializeHeader(m.header)),
    fsCt: await toBase64Url(m.ct),
    senderTTL,
  };
  if (fsPre) payload.fsPre = fsPre;
  if (senderSigil) payload.senderSigil = senderSigil;
  if (recipientSigil) payload.recipientSigil = recipientSigil;
  const self = await encryptForSelf(text, myBoxPub, mySecKeyBox);
  if (self) { payload.selfCipher = self.cipher; payload.selfNonce = self.nonce; }

  const res = await callFn('sendMessage')(payload);
  await saveSession(convKey, working); // solo dopo invio riuscito
  return res.data;
}

/**
 * Invia un ALLEGATO forward-secret. La chiave del file + la caption viaggiano
 * INSIEME nel canale ratchet (payload JSON {k,c}); il blob resta cifrato con la
 * chiave-file (XChaCha). Lancia 'PEER_PREKEYS_MISSING' se il peer non ha prekey.
 * @returns {Promise<{chatId,msgId,...}>}
 */
export async function sendFsAttachment({ mnemonic, boxKeypair, recipientUid, recipientSigil, senderSigil, file, caption, senderTTL, myBoxPub, mySecKeyBox, downloadable }) {
  await ensureSodium();
  const mime = file.type || 'application/octet-stream';
  let blob; let kind; let attachmentName = null; let outMime;
  if (mime.startsWith('image/')) { blob = await compressImage(file); kind = 'image'; outMime = 'image/jpeg'; }
  else if (mime.startsWith('video/')) { await validateVideo(file); blob = file; kind = 'video'; outMime = mime; }
  else if (mime.startsWith('audio/')) { if (file.size > MAX_FILE_BYTES) throw new Error('AUDIO_TOO_LARGE (max 75MB)'); blob = file; kind = 'audio'; outMime = mime; }
  else { if (file.size > MAX_FILE_BYTES) throw new Error('FILE_TOO_LARGE (max 75MB)'); blob = file; kind = 'file'; outMime = mime; attachmentName = (file.name || 'file').slice(0, 120); }

  const fileKey = generateAttachmentKey();
  const { encryptedBlob } = await encryptBlobToStream(blob, fileKey);
  const placeholder = kind === 'image' ? '📷' : kind === 'video' ? '🎬' : kind === 'audio' ? '🎙' : `📎 ${attachmentName || 'file'}`;
  const capText = (caption && caption.trim()) ? caption.trim().slice(0, 500) : placeholder;
  // Payload FS: chiave-file + caption insieme, cifrati col ratchet.
  const payload = JSON.stringify({ k: await toBase64Url(fileKey), c: capText });

  const convKey = convKeyOf(recipientSigil);
  let session = await loadSession(convKey);
  let fsPre = null;
  if (!session) {
    const o = await openOutgoing({ mnemonic, boxKeypair, peerSigil: recipientSigil });
    session = o.session; fsPre = o.prekeyMsg;
  }
  const working = clone(session);
  const m = await encryptMsg(working, padText(payload)); // padding anche per gli allegati

  const sm = {
    recipientUid, v: 2,
    fsHeader: await toBase64Url(serializeHeader(m.header)),
    fsCt: await toBase64Url(m.ct),
    senderTTL, kind, attachmentMime: outMime,
  };
  if (fsPre) sm.fsPre = fsPre;
  if (senderSigil) sm.senderSigil = senderSigil;
  if (recipientSigil) sm.recipientSigil = recipientSigil;
  if (attachmentName) sm.attachmentName = attachmentName;
  if (downloadable) sm.downloadable = true;
  // Copia-per-sé (box-to-self del JSON): il MITTENTE riapre il proprio allegato.
  const self = await encryptForSelf(payload, myBoxPub, mySecKeyBox);
  if (self) { sm.selfCipher = self.cipher; sm.selfNonce = self.nonce; }

  const res = await callFn('sendMessage')(sm);
  const data = res.data;
  if (!data?.chatId || !data?.msgId) throw new Error(data?.error || 'SEND_FAILED');
  try {
    await uploadEncryptedBlob(data.chatId, data.msgId, encryptedBlob, kind);
  } catch (e) {
    try { await callFn('unsendMessage')({ chatId: data.chatId, msgId: data.msgId }); } catch {}
    throw new Error(`UPLOAD_FAILED: ${e?.message || e}`);
  }
  await saveSession(convKey, working); // avanza il ratchet solo a invio+upload riusciti
  return data;
}

/** Estrae la chiave-file (Uint8Array) dal payload FS già decifrato (JSON {k,c}). */
export async function fsAttachmentKeyFromPayload(jsonText) {
  const j = JSON.parse(jsonText);
  return fromBase64Url(j.k);
}

/**
 * Decifra un messaggio forward-secret ricevuto (v2). Retry-safe: opera su copia.
 * @param {object} p
 * @param {object} [p.boxKeypair] {pub,priv}
 * @param {string} [p.mnemonic]
 * @param {object} p.msg          { v, fsHeader, fsCt, fsPre? }
 * @param {string} p.peerSigil    sigil del MITTENTE (chiave di conversazione)
 * @returns {Promise<string>} testo in chiaro
 */
export async function decryptFsMessage({ mnemonic, boxKeypair, msg, peerSigil }) {
  if (Number(msg?.v) !== 2 || !msg?.fsHeader || !msg?.fsCt) throw new Error('NOT_FS_MESSAGE');
  const convKey = convKeyOf(peerSigil);
  const header = parseHeader(await fromBase64Url(msg.fsHeader));
  const ct = await fromBase64Url(msg.fsCt);

  let session = await loadSession(convKey);
  let consumeOpkId = null;
  if (!session) {
    if (!msg.fsPre) throw new Error('NO_SESSION_NO_PREKEY');
    const mySpk = await loadMySpk(msg.fsPre.spkId);
    if (!mySpk) throw new Error('SPK_NOT_FOUND');
    const myOpk = msg.fsPre.opkId ? await peekMyOpk(msg.fsPre.opkId) : null; // peek: consumo dopo
    const myPq = msg.fsPre.pqCt ? await loadMyPq(msg.fsPre.spkId) : null; // segreto KEM per ibrido PQ
    const inc = await openIncoming({ mnemonic, boxKeypair, prekeyMsg: msg.fsPre, mySpk, myOpk, myPq });
    session = inc.session;
    consumeOpkId = msg.fsPre.opkId || null;
  }
  const working = clone(session);
  const text = await decryptMsg(working, header, ct); // lancia se MAC fallisce → niente save
  if (consumeOpkId) await consumeMyOpk(consumeOpkId);
  await saveSession(convKey, working);
  return unpadText(text); // rimuove il padding → testo (o JSON allegato) originale
}

/**
 * Pubblica le prekey del device se mancano/quasi finite, e persiste i privati.
 * Idempotente: salta se il device ha già abbastanza OPK locali.
 * @param {object} p
 * @param {object} p.signKeypair {pub,priv} identità sign (firma la SPK)
 * @param {string} p.sigil
 */
export async function ensureMyPrekeysPublished({ signKeypair, sigil, minOpks = 3, count = 20 }) {
  try {
    if (!sigil || !signKeypair) return false;
    if ((await myOpkCount()) >= minOpks) return false; // già a posto su questo device
    const priv = await publishMyPrekeys({ signKeypair, sigil, count });
    await saveMyPrekeys(priv);
    return true;
  } catch (e) {
    console.warn('ensureMyPrekeysPublished failed', e);
    return false;
  }
}
